import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  User, 
  Mail, 
  Bell, 
  MessageSquare,
  Save,
  Camera,
  Globe,
  Lock,
  Palette,
  Moon,
  RotateCcw
} from 'lucide-react'

const Settings = () => {
  return (
    <div className="w-full">
      <Card className="p-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-bold text-gray-800 mb-8">系统设置</h2>

          <div className="grid grid-cols-12 gap-8">
            {/* 左侧：头像和基本信息 */}
            <div className="col-span-12 md:col-span-5">
              {/* 头像设置 */}
              <div className="flex flex-col items-center space-y-4 mb-8">
                <div className="relative">
                  <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center overflow-hidden">
                    <img 
                      src="/images/avatars/cute-cat.png"
                      alt="avatar"
                      className="w-[80%] h-[80%] object-contain"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="absolute bottom-0 right-0 rounded-full w-8 h-8 p-0 flex items-center justify-center bg-white hover:bg-primary/5"
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm text-gray-500">点击更换头像</p>
              </div>

              {/* 基本信息 */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">昵称</label>
                  <div className="relative">
                    <Input 
                      defaultValue="艾小喵" 
                      className="pl-10"
                    />
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">邮箱</label>
                  <div className="relative">
                    <Input 
                      type="email" 
                      defaultValue="user@example.com" 
                      className="pl-10"
                    />
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">个人简介</label>
                  <textarea
                    className="w-full min-h-[100px] p-4 rounded-xl border border-gray-200 focus:border-primary/20 focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                    placeholder="介绍一下你自己..."
                  />
                </div>
              </div>
            </div>

            {/* 右侧：通知和偏好设置 */}
            <div className="col-span-12 md:col-span-7 space-y-8">
              {/* 通知设置 */}
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6">通知设置</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-primary/20 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 mb-1">邮件通知</p>
                        <p className="text-sm text-gray-500">接收重要更新和活动信息</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex items-center gap-2 min-w-[100px]"
                    >
                      <Bell className="w-4 h-4" />
                      <span>开启</span>
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-primary/20 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <MessageSquare className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 mb-1">消息提醒</p>
                        <p className="text-sm text-gray-500">接收互动和评论通知</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex items-center gap-2 min-w-[100px]"
                    >
                      <Bell className="w-4 h-4" />
                      <span>开启</span>
                    </Button>
                  </div>
                </div>
              </div>

              {/* 偏好设置 */}
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6">偏好设置</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-primary/20 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <Globe className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 mb-1">语言</p>
                        <p className="text-sm text-gray-500">选择界面显示语言</p>
                      </div>
                    </div>
                    <select className="cute-select px-4 py-2">
                      <option>简体中文</option>
                      <option>English</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-primary/20 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <Moon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 mb-1">深色模式</p>
                        <p className="text-sm text-gray-500">调整界面亮度</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex items-center gap-2 min-w-[100px]"
                    >
                      <span>关闭</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex justify-end items-center gap-2 pt-8">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => {
                // 重置逻辑
              }}
            >
              <RotateCcw className="w-4 h-4" />
              <span>重置</span>
            </Button>

            <Button
              className="flex items-center gap-2 bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90"
              onClick={() => {
                // 保存逻辑
              }}
            >
              <Save className="w-4 h-4" />
              <span>保存</span>
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default Settings 